import { PropsWithChildren, useEffect, useState } from "react"
import { Card, CommandButton, FooterMenu, HeaderMenu, PrintTransactionModal, TableHeader } from "components"
import { Acc, AddReminder, Date, Deliver, Memo, PromisedDate, RecurringButton, Supplier, SupplierId, TableSection, TaxInclusive, TradingTerm } from "../components"
import { AttachmentModal, Code, Tab, Table } from "./components"
import { DataJournalContextProvider } from "contexts"
import { ReminderContext } from "../contexts"
import { PurchaseOrderContext } from "./contexts"
import { FormProvider, useForm, useFormContext } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import moment from "moment"
import toast from "react-hot-toast"
import { useLocation } from "react-router-dom"
import { 
  useApi, 
  freightAlert, 
  useToggle,
  convertNumber, 
  useTaxCode,
  useCountryById,
  useHandleAuth,
  useProvinceById,
  useRegencyById,
  useSubdistrictById,
} from "utils"
import { type FormType, validation_schema, getFinalValue } from "./utils"
import { useList } from "pages/Setup/Preference/SettingCompany/utils/hooks"
import { useSubdomainSetting, useVendorDetail } from "./utils"
import { useProduct } from "./components/DetailBody/utils"
import { useSupplier } from "../components/Supplier/utils"
import { usePurchaseFromStockStore } from "pages/Inventori/Report/ItemDetailStockGlobal/store/usePurchaseFromStockStore"
import { PERMISION } from "security"

export default function PurchaseOrder() {
  const api = useApi() 
  const location = useLocation()
  const { isActive, toggle } = useToggle(false)

  const default_values: FormType = {
    _vendor_name: "",
    approval_status: 0,
    address: "",
    freight: 0,
    location_id: "",
    memo: "Purchase order transaction",
    payment_term: "",
    promize_date: moment().format("YYYY-MM-DD"),
    referensi: "",
    tax_inclusive: true,
    transaction_date: moment().format("YYYY-MM-DD"),
    vendor_id: "",
    podetail: [],
    reminder: null,
    imgPath: []
  }
  const onSubmit = (value: FormType) => {
    // Vars
    const final_value = getFinalValue(value)

    return new Promise<void>((resolve) => {
      // Functions
      const submitForm = (): void => {
        toast.promise(
          api.post("/purchaseorder/add", final_value),
          {
            loading: "Loading...",
            success: (res) => res.data.message,
            error: (err) => err.response.data.detail?.message ?? err.response.data.message
          }
        ).then(() => {
          methods.reset()

          // Scroll to Top
          const overflowElement = document.querySelector('#overflow-container')
          overflowElement!.scrollTo({
            top: 0,
            behavior: 'smooth'
          })
        }).catch(() => { }).finally(resolve)
      }

      if (!value.freight) {
        freightAlert("Purchase Order").then(() => {
          submitForm()
        }).catch(() => {
          return resolve()
        })
      } else {
        submitForm()
      }
    })
  }
  const methods = useForm<FormType>({
    defaultValues: default_values,
    resolver: yupResolver(validation_schema)
  })
  // console.log(methods?.control?._formValues)


  //get data from store
  const { item: stockItem, clear } = usePurchaseFromStockStore()

  useEffect(() => {
    if (!stockItem) return
    console.log(stockItem);
    
    const inject = async () => {
      try {
        const res = await api.get("/vendor/get_by_product_id", {
          params: { product_id: stockItem.product_id }
        })

        const projectRes = await api.get("/project/");
        const projects = projectRes.data.payload ?? [];

        const defaultProjectId = projects.length > 0 ? projects.find( (proj: any) => proj.project_name === "General Operation" )?.project_id ?? "": "";
        const vendor = res.data.payload
        if (vendor) {
          methods.setValue("vendor_id", String(vendor.vendor_id)) 
          methods.setValue("_vendor_name", vendor.vendor_name)
        }
  
        const total = stockItem.qty_order * stockItem.price_measure
        methods.setValue("podetail", [
          {
            product_id: stockItem.product_id,
            qty_order: stockItem.qty_order,
            delivered: 0,
            discount: 0,
            mr_unit_id: String(stockItem.mr_unit_id),
            price_measure: stockItem.price_measure ?? res.data.buying_price,
            total,
            project_id: defaultProjectId.toString(),
            tax_id: "",
            description: stockItem.product_name,
            vendor_description: stockItem.product_name,
          },
        ])
  
        clear()
      } catch {
        toast.error("Failed to prepare purchase order")
      }
    }
  
    inject()
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockItem])

  useEffect(() => {
    // Vars
    const state = location.state

    if (state) {
      methods.setValue("vendor_id", state.vendor_id)
      methods.setValue("_vendor_name", state._vendor_name)
    }

    // eslint-disable-next-line
  }, [location.state])

  return (
    <PurchaseOrderContext.Provider value={{ disabled: false }}>
      <FormProvider {...methods}>
        <section className="container my-5 flex flex-col gap-2">
          <HeaderMenu title="DATA ENTRY | PURCHASE ORDER" className="sticky top-[30px] z-[2]" action="create">
            <Code />
          </HeaderMenu>

          <section className="grid lg:grid-cols-3 gap-x-6 gap-y-3 items-end">
            <Supplier
              name="vendor_id"
              trading_term_name="payment_term"
              vendor_name="_vendor_name"
              detail="podetail"
            />

            <TradingTerm name="payment_term" />
            <TaxInclusive name="tax_inclusive" isHidden />
          </section>

          <Card>
            <Card.Body className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
              <section className="flex flex-col gap-3">
                <Deliver
                  deliver_name="location_id"
                  detail_name="address"
                />

                <Memo name="memo" />
              </section>

              <section className="flex flex-col gap-3">
                <Date name="transaction_date" />
                <SupplierId name="referensi" />
                <PromisedDate name="promize_date" />

                <ReminderContext.Provider
                  value={{
                    disabled: false,
                    transaction_from: "PURCHASE ORDER",
                    name: {
                      memo: "memo",
                      promise_date: "promize_date",
                      reminder: "reminder",
                      referensi: "referensi",
                      transaction_date: "transaction_date",
                      vendor_name: "_vendor_name",
                      reminder_child: {
                        memo: "reminder.memo",
                        promise_date: "reminder.promise_date",
                        transaction_date: "reminder.transaction_date"
                      }
                    },
                    onSuccess: (value) => {
                      methods.setValue("reminder.memo", value.memo)
                      methods.setValue("reminder.promise_date", value.promise_date)
                      methods.setValue("promize_date", value.promise_date)
                      methods.setValue("reminder.transaction_date", value.transaction_date)
                    }
                  }}
                >
                  <AddReminder />
                </ReminderContext.Provider>
              </section>
            </Card.Body>
          </Card>

          <DataJournalContextProvider>
            <Table />

            <section className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
              <section className="flex flex-col gap-3">
                <TableSection>
                  <TableSection.Row
                    label="DELIVERY STATUS"
                    value="-"
                  />

                  <TableSection.Row
                    label="PO STATUS"
                    value="-"
                  />

                  <TableSection.Row
                    label="APPROVED BY"
                    value="-"
                  />
                </TableSection>

                <RecurringButton />
              </section>

              <Acc
                name={{
                  detail: "podetail",
                  freight: "freight"
                }}
              />
            </section>
          </DataJournalContextProvider>

          <Tab />

          <FooterMenu
            hasCancelButton
            save={{
              permission: PERMISION.PURCHASES.TRANSACTION.CREATE_PURCHASE_ORDER.CREATE,
              actiontype: "save",
              type: "button",
              color: "primary",
              loading: methods.formState.isSubmitting ? "true" : undefined,
              onClick: methods.handleSubmit(onSubmit, (errors) => {
                console.error("FORM VALIDATION ERROR:", errors)

                // 1. Header error
                if (errors.location_id?.message) {
                  toast.error(errors.location_id.message)
                  return
                }

                if (errors.payment_term?.message) {
                  toast.error(errors.payment_term.message)
                  return
                }

                // 2. Podetail error
                if (Array.isArray(errors.podetail) && errors.podetail.length > 0) {
                  const rowError = errors.podetail[0]
                  const firstFieldError:any = Object.values(rowError)[0]

                  if (firstFieldError?.message) {
                    toast.error(firstFieldError.message)
                    return
                  }
                }

                toast.error("Please complete required fields")
              })
            }}
            
            customElement={{ 
              attachment: <AttachmentModal />,
              print : <CommandButton actiontype="print" onClick={() => toggle()} />
             }}
          />
          {isActive ? (
            <Print
              withImage={false}
              toggle={toggle}
            />
          ) : null}

        </section>
      </FormProvider>
    </PurchaseOrderContext.Provider>
  )
}

const Print = (params: PropsWithChildren<PrintType>): JSX.Element => {
  const methods = useFormContext<FormType>()
  const defaultValues = methods.control._formValues as any

  const product = useProduct()
  const setting = useSubdomainSetting()
  const taxCode = useTaxCode()
  const vendor = useVendorDetail(defaultValues?.vendor_id)
  const company = useList()
  const country = useCountryById()
  const province = useProvinceById()
  const regency = useRegencyById()
  const subdistrict = useSubdistrictById()
  const supplier = useSupplier()
  const { userTokenData } = useHandleAuth()

  // Vars
  const [address, setAddress] = useState<string>("")
  const freight: number = defaultValues?.freight ?? 0
  const detail = defaultValues?.podetail ?? []
  const subtotal: number = detail.reduce((acc: any, item: any): number => {
    return acc + (item?.total ?? 0)
  }, 0)
  const tax: number = detail.reduce((acc: any, item: any): number => {
    // Vars
    const tax_item = taxCode.data.find((i) => i.value === item?.tax_id)

    return acc + ((item?.total ?? 0) * (tax_item?.rate ?? 0)) / 100
  }, 0)
  const total = subtotal + freight + tax
  const userToken = userTokenData()

  useEffect(() => {
    if (defaultValues?.vendor_id) {
      product.refetch(defaultValues.vendor_id)
    }

    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    company.fetchData().then((res) => {
      setAddress(res.address)

      if (res.subdistrict_id) {
        subdistrict.fetchData(res.subdistrict_id)
      }

      if (res.regency_id) {
        regency.fetchData(res.regency_id)
      }

      if (res.province_id) {
        province.fetchData(res.province_id)
      }

      if (res.country_id) {
        country.fetchData(res.country_id)
      }
    })

    // eslint-disable-next-line
  }, [])

  // Functions
  const getAddress = (): string => {
    let generateAddress = address ?? ""

    if (subdistrict.data) {
      generateAddress += ", " + subdistrict.data.subdistrict_name
    }

    if (regency.data) {
      generateAddress += ", " + regency.data.regency_name
    }

    if (province.data) {
      generateAddress += ", " + province.data.province_name
    }

    if (country.data) {
      generateAddress += ", " + country.data.country_name
    }

    return generateAddress
  }

  // Functions
  const getVendorAddress = (): string => {
    const data = supplier.data.find(item => item.value === defaultValues?.vendor_id)

    let generateAddress = data?.address ?? ""

    if (data?.subdistrict_name) {
      generateAddress += ", " + data.subdistrict_name
    }

    if (data?.regency_name) {
      generateAddress += ", " + data.regency_name
    }

    if (data?.province_name) {
      generateAddress += ", " + data.province_name
    }

    if (data?.country_name) {
      generateAddress += ", " + data.country_name
    }

    return generateAddress
  }

  return (
    <PrintTransactionModal
      isLoading={company.isLoading || supplier.isLoading || country.isLoading || province.isLoading || subdistrict.isLoading || regency.isLoading}
      toggle={params.toggle}
    >
      <section className="flex flex-col gap-3">
        <div className="text-center text-2xl font-bold uppercase">
          {userToken?.company_name}
        </div>

        <section className="grid grid-cols-2 items-stretch gap-1">
          <section className="font-bold">
            <table className="table-top">
              <tbody className="font-bold">
                <tr>
                  <td className="pr-2">ADDRESS</td>
                  <td>: {getAddress()}</td>
                </tr>

                <tr>
                  <td>PHONE</td>
                  <td>
                    {" "}
                    : {setting.data?.phone ? setting.data?.phone : "-"}
                  </td>
                </tr>

                <tr>
                  <td>EMAIL</td>
                  <td>
                    {" "}
                    : {setting.data?.email ? setting.data?.email : "-"}
                  </td>
                </tr>

                <tr>
                  <td>WEBSITE</td>
                  <td>
                    : {setting.data?.website ? setting.data?.website : "-"}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="flex flex-col justify-between items-end">
            <div className="text-2xl font-bold text-right">
              PURCHASE ORDER
            </div>

            <table>
              <tbody className="font-bold ">
                <tr>
                  <td width={10} className="text-right">
                    DATE :{" "}
                  </td>
                  <td className="whitespace-nowrap text-right">
                    {defaultValues?.transaction_date
                      ? moment(defaultValues?.transaction_date).format(
                          "DD/MM/YYYY"
                        )
                      : "-"}
                  </td>
                </tr>

                <tr>
                  <td width={10} className="whitespace-nowrap">
                    PURCHASE ORDER NO :{" "}
                  </td>
                  <td className="whitespace-nowrap text-right">
                    {defaultValues?.referensi}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
        </section>

        <table className="table table-bordered">
          <tbody>
            <tr className="bg-black text-white font-bold">
              <td colSpan={2}>VENDOR INFORMATION</td>
            </tr>

            <tr>
              <td>
                <div>VENDOR NAME</div>
                <div className="font-bold">{defaultValues?._vendor_name}</div>
              </td>

              <td>
                <div>CREATED BY</div>
                <div className="font-bold">
                  {/* {createdBy.data?.employee_name} */}
                  -
                </div>
              </td>
            </tr>

            <tr>
              <td colSpan={2}>
                <div>ADDRESS</div>
                <div className="font-bold">{getVendorAddress()}</div>
              </td>
            </tr>

            <tr>
              <td>
                <div>CONTACT NO</div>
                <div className="font-bold">{vendor.data?.handphone}</div>
              </td>

              <td>
                <div>EMAIL ADDRESS</div>
                <div className="font-bold">{vendor.data?.email}</div>
              </td>
            </tr>
          </tbody>
        </table>

        <table className="table table-bordered">
          <TableHeader>
            <tr className="bg-black text-white">
              <th>#</th>
              {params.withImage ? <th>PICTURE</th> : null}
              <th>DESCRIPTION</th>
              <th>ORDER</th>
              <th>BACKORDER</th>
              <th>RECEIVED</th>
              <th>UNIT</th> 
              <th>PRICE</th>
              <th>DISC</th>
              <th>TOTAL</th>
              <th>TAX</th>
            </tr>
          </TableHeader>

          <tbody>
            {defaultValues?.podetail?.map((item: any, key: any) => {
              // Vars
              const productData = product.data.find(
                (i) => i.value === item?.product_id
              )
              const taxCodeData = taxCode.data.find(
                (i) => i.value === item?.tax_id
              )
              const imagePath = `${process.env.REACT_APP_BASE_URL_API}/getimage/?imgpath=${productData?.product_foto}`

              return (
                <tr>
                  <td className="text-center">{key + 1}</td>
                  {params.withImage ? (
                    <td width={150}>
                      <img
                        width={150}
                        height={150}
                        src={imagePath}
                        alt={productData?.product_name}
                      />
                    </td>
                  ) : null}
                  <td>
                    <div>{productData?.product_barcode}</div>
                    <div className="mb-4">{item?.description}</div>
                    <div>COLOUR : {productData?.mr_product_colour_name}</div>
                    <div>STYLE : {productData?.mr_product_style_name}</div>
                    <div>WEIGHT : {productData?.weight}</div>
                    <div>UNIT : {productData?.mr_unit_name}</div>
                  </td>
                  <td className="text-right">
                    {convertNumber(item?.qty_order ?? 0).intoCurrency}
                  </td>
                  <td className="text-right">
                    {convertNumber(item?.delivered ?? 0).intoCurrency}
                  </td>
                  <td className="text-right">
                    {
                      convertNumber(
                        (item?.qty_order ?? 0) - (item?.delivered ?? 0)
                      ).intoCurrency
                    }
                  </td>
                  <td>{productData?.mr_unit_name}</td>
                  <td className="text-right">
                    {convertNumber(item?.price_measure ?? 0).intoCurrency}
                  </td>
                  <td className="text-right">
                    {convertNumber(item?.discount ?? 0).intoCurrency}%
                  </td>
                  <td className="text-right">
                    {convertNumber(item?.total ?? 0).intoCurrency}
                  </td>
                  <td>{taxCodeData?.label}</td>
                </tr>
              )
            })}
          </tbody>
        </table>

        <section className="grid grid-cols-5 items-stretch gap-5">
          <section className="col-span-2 flex flex-col gap-3">
            <table className="table table-bordered">
              <tbody>
                <tr className="bg-black text-white font-bold">
                  <td>Additional Notes:</td>
                </tr>

                <tr className="min-h-[300px]">
                  <td>{defaultValues?.memo}</td>
                </tr>
              </tbody>
            </table>

            <section>
              <div className="w-full bg-black text-white font-bold">Authorized By</div>
              <div className="h-[100px]" />
              <div>{userToken?.company_name}</div>
              {/* <div>{approvedBy.data?.job_position_name}</div> */}
              -
            </section>
          </section>
          <div />

          <section className="col-span-2">
            <table className="w-full break-inside-avoid">
              <tbody>
                <tr className="font-bold">
                  <td>SUBTOTAL</td>
                  <td className="text-right border-b w-[100px]">
                    {convertNumber(subtotal).intoCurrency}
                  </td>
                </tr>

                <tr>
                  <td>FREIGHT</td>
                  <td className="text-right border-b">
                    {convertNumber(freight).intoCurrency}
                  </td>
                </tr>

                <tr>
                  <td>TAX</td>
                  <td className="text-right border-b">
                    {convertNumber(tax).intoCurrency}
                  </td>
                </tr>

                <tr>
                  <td>TOTAL AMOUNT</td>
                  <td className="text-right border-b">
                    {convertNumber(total).intoCurrency}
                  </td>
                </tr>

                <tr>
                  <td>PAYMENT</td>
                  <td className="text-right border-b">
                    {convertNumber(0).intoCurrency}
                  </td>
                </tr>

                <tr className="font-bold">
                  <td>BALANCE</td>
                  <td className="text-right border-b border-double">
                    {convertNumber(total).intoCurrency}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
        </section>
      </section>
    </PrintTransactionModal>
  )
}
type PrintType = {
  withImage?: boolean
  toggle: () => void
}